import React, { useEffect, useMemo, useState } from "react";
import * as signalR from "@microsoft/signalr";
import { authFetch, getAuth, getToken, isAdmin } from "../../auth";
import { hubUrl } from "../../appConfig";
import { formatDateTimeText, formatSourceText, getDefaultDateTimeRange, toUtcDateTimeQueryValue } from "./logHelpers";
import { fetchCustomerOptions, fetchDeviceOptions, fetchGroupOptions, formatCustomerLabel, formatDeviceLabel } from "./logFilterOptions";

const PAGE_SIZE = 20;

export function ConnectionLogPage() {
  const admin = isAdmin();
  const auth = getAuth();
  const myCustomerId = auth?.customerId ? String(auth.customerId) : "";
  const defaultTimeRange = useMemo(() => getDefaultDateTimeRange(), []);

  const [source, setSource] = useState("");
  const [logType, setLogType] = useState("");
  const [keyword, setKeyword] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [deviceNo, setDeviceNo] = useState("");
  const [startTime, setStartTime] = useState(defaultTimeRange.startTime);
  const [endTime, setEndTime] = useState(defaultTimeRange.endTime);

  const [customers, setCustomers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [devices, setDevices] = useState([]);
  const [optionError, setOptionError] = useState("");

  const [page, setPage] = useState(1);
  const [queryVersion, setQueryVersion] = useState(0);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);
  const optionCustomerId = admin ? customerId : myCustomerId;

  useEffect(() => {
    if (!admin) return;
    loadCustomerOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);

  useEffect(() => {
    loadLogs(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, queryVersion]);

  useEffect(() => {
    loadGroupOptions(optionCustomerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionCustomerId]);

  useEffect(() => {
    loadDeviceOptions(optionCustomerId, groupId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionCustomerId, groupId]);

  useEffect(() => {
    const token = getToken();
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl("/hubs/gps"), {
        accessTokenFactory: () => token
      })
      .withAutomaticReconnect()
      .build();

    connection.on("ConnectionLogCreated", () => {
      setQueryVersion((v) => v + 1);
    });

    connection.start().catch(() => {});
    return () => {
      connection.stop();
    };
  }, []);

  async function loadCustomerOptions() {
    try {
      setCustomers(await fetchCustomerOptions());
      setOptionError("");
    } catch (err) {
      setCustomers([]);
      setOptionError(err?.message || "客户列表加载失败");
    }
  }

  async function loadGroupOptions(nextCustomerId) {
    if (admin && !nextCustomerId) {
      setGroups([]);
      return;
    }

    try {
      setGroups(await fetchGroupOptions(nextCustomerId));
      setOptionError("");
    } catch (err) {
      setGroups([]);
      setOptionError(err?.message || "分组列表加载失败");
    }
  }

  async function loadDeviceOptions(nextCustomerId, nextGroupId) {
    if (admin && !nextCustomerId) {
      setDevices([]);
      return;
    }

    try {
      setDevices(await fetchDeviceOptions({ customerId: nextCustomerId, groupId: nextGroupId }));
      setOptionError("");
    } catch (err) {
      setDevices([]);
      setOptionError(err?.message || "车辆列表加载失败");
    }
  }

  function onCustomerChange(value) {
    setCustomerId(value);
    setGroupId("");
    setDeviceNo("");
  }

  function onGroupChange(value) {
    setGroupId(value);
    setDeviceNo("");
  }

  async function loadLogs(targetPage) {
    setLoading(true);
    setError("");

    try {
      const qs = new URLSearchParams({
        page: String(targetPage),
        pageSize: String(PAGE_SIZE)
      });

      if (source) qs.set("source", source);
      if (logType !== "") qs.set("logType", logType);
      if (keyword.trim()) qs.set("keyword", keyword.trim());
      if (admin && customerId.trim()) qs.set("customerId", customerId.trim());
      if (groupId.trim()) qs.set("groupId", groupId.trim());
      if (deviceNo.trim()) qs.set("deviceNo", deviceNo.trim());

      const startUtc = toUtcDateTimeQueryValue(startTime);
      const endUtc = toUtcDateTimeQueryValue(endTime);
      if (startUtc) qs.set("startTimeUtc", startUtc);
      if (endUtc) qs.set("endTimeUtc", endUtc);

      const res = await authFetch(`/api/logs/connections?${qs.toString()}`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "连接日志查询失败");
      }

      const data = await res.json();
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
    } catch (err) {
      setError(err?.message || "连接日志查询失败");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  function onSearch(e) {
    e.preventDefault();
    setPage(1);
    setQueryVersion((v) => v + 1);
  }

  function onReset() {
    setSource("");
    setLogType("");
    setKeyword("");
    setCustomerId("");
    setGroupId("");
    setDeviceNo("");
    const nextTimeRange = getDefaultDateTimeRange();
    setStartTime(nextTimeRange.startTime);
    setEndTime(nextTimeRange.endTime);
    setPage(1);
    setQueryVersion((v) => v + 1);
  }

  return (
    <div className="container-fluid">
      <div className="card shadow-sm status-card mt-3">
        <div className="card-header bg-primary text-white">连接日志</div>
        <div className="card-body">
          <form className="row g-2 align-items-end" onSubmit={onSearch}>
            <div className="col-md-2">
              <label className="form-label">来源</label>
              <select className="form-select" value={source} onChange={(e) => setSource(e.target.value)}>
                <option value="">全部</option>
                <option value="device">设备</option>
                <option value="web">Web</option>
                <option value="unknown">未知</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">类型</label>
              <select className="form-select" value={logType} onChange={(e) => setLogType(e.target.value)}>
                <option value="">全部</option>
                <option value="1">登入</option>
                <option value="0">登出</option>
              </select>
            </div>
            {admin && (
              <div className="col-md-2">
                <label className="form-label">客户</label>
                <select className="form-select" value={customerId} onChange={(e) => onCustomerChange(e.target.value)}>
                  <option value="">全部客户</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={String(customer.id)}>
                      {formatCustomerLabel(customer)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="col-md-2">
              <label className="form-label">分组</label>
              <select className="form-select" value={groupId} onChange={(e) => onGroupChange(e.target.value)} disabled={admin && !customerId}>
                <option value="">{admin && !customerId ? "请先选择客户" : "全部分组"}</option>
                {groups.map((group) => (
                  <option key={group.id} value={String(group.id)}>
                    {group.name}{group.deviceCount != null ? ` (${group.deviceCount})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">车辆</label>
              <select className="form-select" value={deviceNo} onChange={(e) => setDeviceNo(e.target.value)} disabled={admin && !customerId}>
                <option value="">{admin && !customerId ? "请先选择客户" : "全部车辆"}</option>
                {devices.map((device) => (
                  <option key={device.id} value={device.deviceNo}>
                    {formatDeviceLabel(device)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">关键字</label>
              <input
                className="form-control"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="客户 / 登录名 / IP"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">开始时间</label>
              <input className="form-control" type="datetime-local" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">结束时间</label>
              <input className="form-control" type="datetime-local" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </div>
            <div className="col-md-2">
              <button className="btn btn-primary w-100" type="submit" disabled={loading}>
                {loading ? "查询中..." : "查询"}
              </button>
            </div>
            <div className="col-md-2">
              <button className="btn btn-outline-secondary w-100" type="button" onClick={onReset} disabled={loading}>
                重置
              </button>
            </div>
          </form>

          {error && <div className="status-banner error mt-3">{error}</div>}
          {optionError && <div className="status-banner warn mt-3">{optionError}</div>}

          <div className="table-responsive mt-3">
            <table className="table table-sm table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>时间</th>
                  <th>来源</th>
                  <th>类型</th>
                  <th>显示名</th>
                  <th>设备号</th>
                  <th>登录名</th>
                  <th>车牌号</th>
                  <th>客户</th>
                  <th>IP</th>
                  <th>端口</th>
                </tr>
              </thead>
              <tbody>
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={10}>
                      <div className="status-banner empty m-0">暂无连接日志</div>
                    </td>
                  </tr>
                )}
                {items.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDateTimeText(row.logTime)}</td>
                    <td>{formatSourceText(row.source)}</td>
                    <td>{row.typeText || "-"}</td>
                    <td>{row.displayName || "-"}</td>
                    <td>{row.deviceNo || "-"}</td>
                    <td>{row.loginName || "-"}</td>
                    <td>{row.vehicleNo || "-"}</td>
                    <td>{row.customerName || (row.customerId != null ? `#${row.customerId}` : "-")}</td>
                    <td>{row.ipAddress || "-"}</td>
                    <td>{row.port ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="text-muted" style={{ fontSize: 13 }}>
              共 {total} 条，当前第 {page}/{pageCount} 页
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                上一页
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                disabled={page >= pageCount || loading}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
